// SHADOW-owned Obelisk surface - typed transition-proof callback helper.
// Single source of truth for exchanging an `obelisk_session` token for a
// verified transition proof through the Worker `/api/obelisk-verify` contract.
// Boundary: this is a transition proof only. It is never production admin
// auth, rights proof, legal approval, or publication approval; `adminAccess`
// stays `false` while SHADOW is pre-launch.

export interface ObeliskTransitionSession {
  verified: boolean;
  role: "artist" | "admin" | "agent";
  sessionStatus: "accepted" | "rejected";
  receipt?: string;
  authBoundary: string;
  adminAccess: false;
  verifiedAt: string;
}

export type ObeliskCallbackResult =
  | { ok: true; session: ObeliskTransitionSession }
  | { ok: false; reason: "no-token" | "verify-failed" | "network-error"; authBoundary?: string };

export const OBELISK_TRANSITION_STORAGE_KEY = "shadow-obelisk-transition";

export interface ObeliskCallbackOptions {
  search?: string;
  verifyEndpoint?: string;
  fetchImpl?: (input: string, init?: RequestInit) => Promise<Response>;
}

export async function handleObeliskCallback(options: ObeliskCallbackOptions = {}): Promise<ObeliskCallbackResult> {
  const {
    search = typeof window === "undefined" ? "" : window.location.search,
    verifyEndpoint = "/api/obelisk-verify",
    fetchImpl = (input, init) => fetch(input, init)
  } = options;

  const token = new URLSearchParams(search).get("obelisk_session");
  if (!token) return { ok: false, reason: "no-token" };

  try {
    const response = await fetchImpl(verifyEndpoint, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token })
    });
    const body = (await response.json()) as Omit<ObeliskTransitionSession, "verifiedAt">;
    if (!response.ok || !body.verified) {
      return { ok: false, reason: "verify-failed", authBoundary: body.authBoundary };
    }
    return { ok: true, session: { ...body, verifiedAt: new Date().toISOString() } };
  } catch {
    return { ok: false, reason: "network-error" };
  }
}
